import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import DescriptionTable from './DescriptionTable';
import TimelineOverview from './TimelineOverview';
import RoundSpecialDetails from './RoundSpecialDetails';
import { getRoundSpecialDetails, getRoundSpecialsTimeline } from '../../selectors/roundSpecials';
import { aGetRoundSpecialDetailsAction } from '../../reducers/roundSpecials';
import { getLeaguesMemoized } from '../../selectors/common';

/**
 * Groups the array of matches by matchMinute and sorts the result by matchMinute.
 *
 * @param {Array} matches - Array of match objects to group and sort.
 * @param {Array} teams - Array of teams in the selected league. It has id and name.
 * @returns {Array} - Array of grouped matches sorted by matchMinute.
 */
function groupMatchesByMinute(matches, teams) {
  const teamMap = teams.reduce((acc, team) => {
    acc[team.id] = team.name;
    return acc;
  }
  , {});

  const groupedMatches = matches.reduce((acc, match) => {
    const { matchMinute } = match;

    if (!acc[matchMinute]) {
      acc[matchMinute] = {
        matchMinute,
        totalScorePerMinute: match.totalScorePerMinute,
        matches: [],
      };
    }

    acc[matchMinute].matches.push({...match, homeTeam: teamMap[match.homeTeamId], awayTeam: teamMap[match.awayTeamId]});
    return acc;
  }, {});

  // Convert the grouped object into an array and sort by matchMinute
  return Object.values(groupedMatches).sort((a, b) => a.matchMinute - b.matchMinute);
}

const RoundSpecialsMatchTimeline = () => {
  const dispatch = useDispatch();
  const params = useParams();

  const details = useSelector(getRoundSpecialDetails);
  const timeline = useSelector(getRoundSpecialsTimeline);
  const leagues = useSelector(getLeaguesMemoized);

  const selectedLeague = leagues.find((league) => league.id === details?.leagueId);
  const groupedMatches = groupMatchesByMinute(timeline || [], selectedLeague?.teams || []);

  useEffect(function loadRoundSpecialTimeline() {
    if (params?.id) {
      dispatch(aGetRoundSpecialDetailsAction({ id: params.id }));
    }
  }, [dispatch, params?.id]);

  return (
    <>
      <DescriptionTable roundId={details?.roundId} publicRoundId={details?.roundPublicId} leagueName={selectedLeague?.name} seasonId={details?.seasonId} />
      <TimelineOverview roundTotalScore={details?.roundTotalScore} minutes={details?.roundMinute} statusId={details?.statusId} />
      <RoundSpecialDetails matches={groupedMatches} />
    </>
  );
};

export default RoundSpecialsMatchTimeline;